/**
 * 도움말 도메인 타입. **서버(core-service docs 모듈) 응답과 1:1** 이다.
 * 한쪽만 바꾸면 사이드바가 비거나 본문이 안 뜬다.
 */

/**
 * 섹션 색 축. 서버 `DocsSectionTone` 값과 같아야 한다.
 *
 * 예전엔 사이드바의 작은 점 배경색이었고, 지금은 **아이콘 색**이다.
 * Tailwind 는 `text-${tone}` 같은 동적 조합을 스캔하지 못하므로 클래스 문자열을
 * 통째로 적어둔다.
 */
export const DOCS_SECTION_TONE = {
  BRAND: 'text-brand-500',
  XCOST_PRO: 'text-xcost-pro',
  XPIXELS: 'text-xpixels-aqc',
  WORKNOTE: 'text-worknote',
  SUCCESS: 'text-success',
  WARNING: 'text-warning',
} as const;

export type DocsSectionTone = keyof typeof DOCS_SECTION_TONE;

export interface DocSummary {
  id: string;
  sectionId: string;
  slug: string;
  title: string;
  summary: string;
  keywords: string[];
  minAppVersion: string | null;
  sortOrder: number;
  isPublished: boolean;
  /** 문서 "최종 수정" 표기의 기준. publishedAt 이 아니다. */
  updatedAt: string;
  /** `/docs/<section>/<slug>` — 서버가 조립해서 준다. */
  path: string;
  /**
   * 도움말 피드백 집계. **관리 트리에서만 온다** — 공개 응답에는 없다.
   * 독자에게 숫자를 보여주면 읽기도 전에 문서를 불신하게 되고, 그 숫자로
   * 독자가 할 수 있는 일도 없다. 어느 문서를 고칠지 정하는 내부 신호다.
   */
  feedback?: DocFeedbackCounts;
}

/** 서버 DTO(`DOCS_LIMITS.FEEDBACK_REASON_MAX`)와 같아야 한다. */
export const DOCS_FEEDBACK_REASON_MAX = 300;

export interface DocFeedbackCounts {
  helpful: number;
  notHelpful: number;
}

/** 관리 화면의 '아니오' 사유 1건. 작성자 식별 정보는 서버가 주지 않는다. */
export interface DocFeedbackEntry {
  id: string;
  reason: string | null;
  createdAt: string;
}

export interface DocFeedbackList {
  counts: DocFeedbackCounts;
  /** 사유 유무와 무관하게 '아니오' 전체 수. */
  total: number;
  entries: DocFeedbackEntry[];
}

/** 피드백 제출 입력. */
export interface FeedbackInput {
  docId: string;
  helpful: boolean;
  reason?: string | null;
}

export interface DocDetail extends DocSummary {
  /** Tiptap JSON */
  body: TiptapDoc;
  sectionSlug: string;
  sectionTitle: string;
  publishedAt: string | null;
  prev: DocSummary | null;
  next: DocSummary | null;
}

export interface DocSection {
  id: string;
  slug: string;
  title: string;
  summary: string;
  /** 아이콘 레지스트리 키(`docsSectionIcon.type.ts`). 모르는 키는 기본 아이콘. */
  icon: string | null;
  tone: DocsSectionTone;
  sortOrder: number;
  isPublished: boolean;
  /**
   * `/docs` 첫 화면 카드에 노출할지. **공개 여부와 다른 축**이다 —
   * false 여도 문서 상세의 사이드바·검색·직접 링크로는 들어갈 수 있다.
   */
  showOnIndex: boolean;
  docs: DocSummary[];
}

/** 옛 경로 → 현재 경로. slug 를 바꿔도 데스크톱 앱 딥링크가 죽지 않게 한다. */
export interface DocRedirect {
  /** `<section>/<slug>` */
  from: string;
  /** `/docs/<section>/<slug>` */
  to: string;
}

export interface DocsTree {
  sections: DocSection[];
  redirects: DocRedirect[];
}

/** 사이드바·검색에서 쓰는 평탄화된 문서 1건. */
export interface FlatDoc {
  section: DocSection;
  doc: DocSummary;
}

/* ── 쓰기 입력 (서버 DTO 와 1:1) ─────────────────────────────── */

/** PUT 은 전체 교체다 — 부분 갱신이 아니므로 모든 필드를 채워 보낸다. */
export interface DocInput {
  sectionId: string;
  slug: string;
  title: string;
  summary: string;
  body: TiptapDoc;
  keywords: string[];
  minAppVersion: string | null;
  sortOrder?: number;
  isPublished: boolean;
}

export interface SectionInput {
  slug: string;
  title: string;
  summary: string;
  icon: string | null;
  tone: DocsSectionTone;
  sortOrder?: number;
  isPublished: boolean;
  showOnIndex: boolean;
}

export interface ReorderInput {
  sections?: { id: string; sortOrder: number }[];
  /** 순서만 바꾼다. 섹션 이동은 문서 수정(PUT)으로 해야 리다이렉트가 남는다. */
  docs?: { id: string; sortOrder: number }[];
}

/* ── Tiptap JSON ─────────────────────────────────────────────── */

export interface TiptapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface TiptapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  marks?: TiptapMark[];
  text?: string;
}

export interface TiptapDoc extends TiptapNode {
  type: 'doc';
}

/* ── 목차 ────────────────────────────────────────────────────── */

export const HEADING_LEVEL = {
  SECTION: 2,
  SUB: 3,
} as const;

/** 목차 한 줄. 목차는 h2 한 단계뿐이다  */
export interface TocHeading {
  id: string;
  number: number;
  text: string;
}
